import { useEffect, useId, useMemo } from 'react'
import { useAppDispatch } from '@/store/store-hooks'
import { UserFilesPollingActions } from '../store/slice'

type UseUserFilesPollingProcessorOptions = {
  /** Optional stable identifier for tests or debugging. A unique hook instance id is used by default. */
  sourceId?: string
  /** Additional file ids this caller wants the shared poller to include. */
  fileIds?: string[]
  /** When false, this hook removes its source and does not register with the poller. */
  enabled?: boolean
}

const EMPTY_FILE_IDS: string[] = []

/**
 * Registers this caller as a polling source for the shared user-files processor.
 * The processor stays alive while at least one source is registered and sleeps
 * when the active sources have nothing to poll.
 */
export function useUserFilesPollingProcessor(options?: UseUserFilesPollingProcessorOptions) {
  const dispatch = useAppDispatch()
  const generatedSourceId = useId()
  const sourceId = options?.sourceId ?? generatedSourceId
  const fileIds = options?.fileIds ?? EMPTY_FILE_IDS
  const enabled = options?.enabled ?? true
  const dedupedFileIds = useMemo(() => {
    const seenIds = new Set<string>()

    return fileIds.filter((fileId) => {
      if (!fileId || seenIds.has(fileId)) return false
      seenIds.add(fileId)
      return true
    })
  }, [fileIds])

  useEffect(() => {
    if (!enabled) {
      dispatch(UserFilesPollingActions.removePollingSource({ sourceId }))
      return
    }

    dispatch(
      UserFilesPollingActions.upsertPollingSource({
        sourceId,
        fileIds: dedupedFileIds,
      })
    )
  }, [dedupedFileIds, dispatch, enabled, sourceId])

  useEffect(() => {
    return () => {
      dispatch(UserFilesPollingActions.removePollingSource({ sourceId }))
    }
  }, [dispatch, sourceId])
}
